/**
 * Orchestrateur du dashboard.
 *
 * À l'OUVERTURE (demande 13/06/2026) : on rejoue les résultats déjà joués en
 * mode simulation — d'abord la phase de groupes (classements animés), le
 * tableau final n'apparaît QUE lorsqu'on atteint les 16es — puis on bascule en
 * direct. Ensuite, à chaque poll : dérivation de l'état officiel → rendu ;
 * nouveau match terminé → Elo + re-simulation Monte Carlo → probas + bracket.
 */
import { deriveOfficialState } from "../app/officialStore.ts";
import type { OfficialState } from "../app/officialStore.ts";
import type { LiveUpdate } from "../app/live.ts";
import { McClient, defaultSimCount } from "../app/mcClient.ts";
import {
  poissonParams,
  modelHomeAdvantage,
  gkRatings,
  shootoutModel,
  gkDivisor,
  groups,
} from "../app/data.ts";
import type { FixedResult, SimulationOutput } from "../engine/montecarlo.ts";
import { renderTodayBand } from "./components/todayBand.ts";
import { renderLiveFocus, type LiveFocusState } from "./components/liveFocus.ts";
import { renderGroupsGrid, renderGroupsSkeleton } from "./components/groupsGrid.ts";
import { renderThirdsTable } from "./components/thirdsTable.ts";
import { renderTeamEfficiency } from "./components/teamEfficiency.ts";
import { renderBracket, fitBracket } from "./components/bracketView.ts";
import { renderProbsPanel, type ProbsMeta } from "./components/probsPanel.ts";
import { renderEloRanking } from "./components/eloRanking.ts";
import { renderLeaderboards, hasLeaderData } from "./components/leaderboards.ts";
import { renderClubRatings, hasClubRatings } from "./components/clubRatings.ts";
import { fetchLeaderboards, type Leaderboards } from "../app/leaderboards.ts";
import { runGroupsIntro, type ReplayHandle } from "./replay.ts";
import { derivedToSimulationOutput, type DerivedProbabilities } from "../app/derived.ts";
import { fmtShortDateTime, fmtCountdown } from "../app/format.ts";
import { t } from "../app/i18n.ts";
import { esc } from "./html.ts";

const ROTATE_MS = 8_000;
const PROBS_HISTORY_KEY = "wc26.probs.history.v1";

export class Dashboard {
  private readonly mc = new McClient();
  private official: OfficialState = deriveOfficialState(null);
  private mcOutput: SimulationOutput | null = null;
  private mcMeta: ProbsMeta | null = null;
  private lastFinishedCount = -1;
  private liveSource = "—";
  private readonly focus: LiveFocusState = { pinned: null, rotationIndex: 0 };
  private rotateTimer: ReturnType<typeof setInterval> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private leaderTimer: ReturnType<typeof setInterval> | null = null;
  /** Jour affiché dans la bande (0 = aujourd'hui ; <0 = jours passés). */
  private dayOffset = 0;
  private replayed = false;
  private inReplay = false;
  private replay: ReplayHandle | null = null;
  /** finishedCount des probas actuellement affichées (serveur ou worker). */
  private probsFinished: number | null = null;
  private probsFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  /** Renseigne si le flux SSE pousse (le serveur calcule alors les probas). */
  private readonly streamingProbe: () => boolean;
  /** Classements joueurs (buteurs/passeurs/notes), servis séparément du live. */
  private leaderData: Leaderboards | null = null;

  private readonly els: {
    replay: HTMLElement;
    today: HTMLElement;
    focus: HTMLElement;
    groups: HTMLElement;
    thirds: HTMLElement;
    bracket: HTMLElement;
    probs: HTMLElement;
    leaders: HTMLElement;
    efficiency: HTMLElement;
    elo: HTMLElement;
    clubs: HTMLElement;
    foot: HTMLElement;
  };

  constructor(container: HTMLElement, options: { streamingProbe?: () => boolean } = {}) {
    this.streamingProbe = options.streamingProbe ?? (() => false);
    // Le focus « En direct » est la PREMIÈRE ligne (avant « Aujourd'hui »).
    container.innerHTML = `
      <section id="sec-replay" class="hide"></section>
      <section id="sec-live" class="hide" aria-live="polite"></section>
      <section id="sec-today" class="hide"></section>
      <section id="sec-groups"></section>
      <section id="sec-thirds" class="hide"></section>
      <section id="sec-bracket" class="hide"></section>
      <section id="sec-probs" class="hide"></section>
      <section id="sec-leaders" class="hide"></section>
      <section id="sec-efficiency" class="hide"></section>
      <section id="sec-elo" class="hide"></section>
      <section id="sec-clubs" class="hide"></section>
      <footer id="sec-foot" class="note foot"></footer>
    `;
    const pick = (id: string): HTMLElement => {
      const el = container.querySelector<HTMLElement>(id);
      if (!el) throw new Error(`section manquante: ${id}`);
      return el;
    };
    this.els = {
      replay: pick("#sec-replay"),
      today: pick("#sec-today"),
      focus: pick("#sec-live"),
      groups: pick("#sec-groups"),
      thirds: pick("#sec-thirds"),
      bracket: pick("#sec-bracket"),
      probs: pick("#sec-probs"),
      leaders: pick("#sec-leaders"),
      efficiency: pick("#sec-efficiency"),
      elo: pick("#sec-elo"),
      clubs: pick("#sec-clubs"),
      foot: pick("#sec-foot"),
    };
    // état d'attente : la grille des groupes vide, le reste masqué
    renderGroupsSkeleton(this.els.groups, groups);
    // resize throttlé (rAF) et seulement si le bracket est visible (pas de
    // layout inutile sur une section cachée / un autre onglet).
    let rafId = 0;
    window.addEventListener("resize", () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (!this.els.bracket.classList.contains("hide")) fitBracket(this.els.bracket);
      });
    });
    // Navigation par jour : onglets de date (aujourd'hui centré). Clic sur un
    // onglet → on charge les matchs de ce jour (et leurs stats au survol).
    this.els.today.addEventListener("click", (e) => {
      const t = e.target;
      const tab = t instanceof Element ? t.closest<HTMLButtonElement>("button.day-tab") : null;
      if (!tab?.dataset.offset) return;
      this.dayOffset = Number(tab.dataset.offset);
      renderTodayBand(this.els.today, this.official, new Date().toISOString(), this.dayOffset);
    });
    this.startRotation();
    this.startCountdown();
    void this.runWorkerSimulation(); // probas pré-tournoi instantanées (état de base)
    // Classements joueurs : 1er chargement + rafraîchissement périodique (le
    // serveur les ré-agrège toutes les 10 min ; côté client on relit tranquillement).
    void this.refreshLeaderboards();
    this.leaderTimer = setInterval(() => void this.refreshLeaderboards(), 10 * 60_000);
  }

  /** Recharge les classements puis les peint (masqués si indisponibles/vides). */
  private async refreshLeaderboards(): Promise<void> {
    this.leaderData = await fetchLeaderboards();
    this.paintLeaders();
  }

  /** Affiche/masque les sections classements (joueurs + clubs) selon les données
   *  et l'état de replay. Même source (leaderboards.json), donc peintes ensemble. */
  private paintLeaders(): void {
    const data = this.leaderData;
    if (this.inReplay || !hasLeaderData(data)) {
      this.els.leaders.classList.add("hide");
      this.els.clubs.classList.add("hide");
      return;
    }
    this.els.leaders.classList.remove("hide");
    renderLeaderboards(this.els.leaders, data);
    // Classement des clubs (note moyenne de leurs internationaux) — masqué si vide.
    if (this.inReplay || !hasClubRatings(data.clubRatings)) this.els.clubs.classList.add("hide");
    else renderClubRatings(this.els.clubs, data.clubRatings);
  }

  /** Point d'entrée : nouvelle donnée live (ou échec) du poller. */
  onLiveUpdate(update: LiveUpdate): void {
    this.official = deriveOfficialState(update.data);
    this.liveSource = update.data?.source ?? "—";
    const finishedCount = this.official.finished.length;
    const finishedChanged = finishedCount !== this.lastFinishedCount;
    this.lastFinishedCount = finishedCount;

    if (this.inReplay) {
      // le replay possède le DOM pendant sa lecture — ne rien re-rendre
    } else if (this.replayed) {
      // par minute : ne re-rendre QUE les zones live ; groupes/Elo/bracket ne
      // changent qu'à la fin d'un match (finishedChanged) — évite de jeter le
      // DOM (focus, scroll) et de recalculer 48 lignes Elo à chaque tick.
      if (finishedChanged) this.renderAll();
      else this.renderLive();
    } else if (this.official.finished.length === 0) {
      // tournoi pas commencé : affichage direct (on n'a pas encore « ouvert »)
      this.revealLiveSections();
      this.renderAll();
    } else if (this.official.liveNow.length > 0) {
      // un match est EN DIRECT à l'ouverture → on l'affiche TOUT DE SUITE, pas
      // de déroulé bloquant. (Le bouton « Rejouer » reste là pour revoir.)
      this.replayed = true;
      this.revealLiveSections();
      this.renderAll();
    } else {
      // rien en direct + des matchs terminés → déroulé de rattrapage animé,
      // forcé même sous « réduire les animations » (demande utilisateur).
      this.startReplay(true);
    }
    // probas : le serveur les pousse (event derived) ; sinon worker local.
    if (finishedChanged) this.ensureProbabilities();
  }

  /** Probabilités calculées par le serveur (SSE). Adoptées si elles concernent
   *  l'état courant ; sinon ignorées (obsolètes). Supprime le besoin du worker. */
  onServerProbs(derived: DerivedProbabilities): void {
    if (derived.finishedCount !== this.official.finished.length) return; // obsolète
    if (this.probsFallbackTimer) {
      clearTimeout(this.probsFallbackTimer);
      this.probsFallbackTimer = null;
    }
    this.mcOutput = derivedToSimulationOutput(derived);
    this.probsFinished = derived.finishedCount;
    this.mcMeta = {
      nSims: derived.nSims,
      elapsedMs: 0,
      computedAt: Date.parse(derived.computedAt) || Date.now(),
      running: false,
      server: true,
    };
    if (!this.inReplay) {
      renderProbsPanel(this.els.probs, this.mcOutput, this.mcMeta);
      if (this.koReached()) renderBracket(this.els.bracket, this.official, this.mcOutput);
      this.renderFooter();
    }
  }

  /** Décide qui calcule les probas pour l'état courant. */
  private ensureProbabilities(): void {
    const fc = this.official.finished.length;
    if (this.probsFinished === fc) return; // déjà à jour
    if (this.probsFallbackTimer) clearTimeout(this.probsFallbackTimer);
    if (this.streamingProbe()) {
      // le serveur va pousser les probas ; worker en filet si rien sous 6 s
      this.probsFallbackTimer = setTimeout(() => {
        if (this.probsFinished !== fc) void this.runWorkerSimulation();
      }, 6000);
    } else {
      void this.runWorkerSimulation();
    }
  }

  /**
   * True seulement quand on ATTEINT réellement les phases finales : tous les
   * groupes terminés, OU un match KO engagé DONT les équipes sont résolues
   * (un « finished » isolé d'un flux, avant la fin des groupes, ne compte pas —
   * ses équipes sont encore des placeholders). Le tableau reste caché avant ça.
   */
  private koReached(): boolean {
    if (this.official.groupsAllComplete) return true;
    return this.official.matches.some((v) => {
      if (v.match.stage === "group") return false;
      if (v.status !== "finished" && v.status !== "live" && v.status !== "paused") return false;
      const resolved = this.official.bracket[`M${v.match.id}`];
      return resolved != null && resolved.a !== null && resolved.b !== null;
    });
  }

  /** Relance le déroulé animé à la demande (bouton), même sous mouvement réduit. */
  replayNow(): void {
    if (this.inReplay) return;
    this.startReplay(true);
  }

  private startReplay(force: boolean): void {
    if (this.official.finished.length === 0) {
      // Rien à rejouer POUR L'INSTANT : affichage direct, mais on NE verrouille
      // PAS `replayed` — l'auto-lecture se déclenchera dès qu'un premier match
      // sera terminé (sinon, un 1er update vide tuerait le replay à jamais).
      this.revealLiveSections();
      this.renderAll();
      return;
    }
    this.inReplay = true;
    // On FIGE l'état rejoué : un poll qui arrive pendant l'animation réassigne
    // this.official, mais l'intro (classements des groupes animés) doit rester
    // cohérente avec l'instantané du début.
    const replayState = this.official;
    // mise en scène « d'abord JUSTE les groupes » : on masque le reste
    for (const el of [
      this.els.focus,
      this.els.today,
      this.els.thirds,
      this.els.bracket,
      this.els.probs,
      this.els.leaders,
      this.els.elo,
      this.els.clubs,
    ]) {
      el.classList.add("hide");
    }
    renderGroupsSkeleton(this.els.groups, groups);
    // Intro : JUSTE les classements de poules qui se réordonnent (sans score),
    // dans l'ordre chronologique ; le bracket/3es/live n'apparaissent qu'APRÈS
    // (revealAfterIntro), via renderAll qui gère leur visibilité.
    this.replay = runGroupsIntro({
      official: replayState,
      headerEl: this.els.replay,
      groupsEl: this.els.groups,
      force,
      onComplete: () => {
        this.inReplay = false;
        this.replayed = true;
        this.revealAfterIntro();
      },
    });
  }

  /** Fin de l'intro : les groupes GLISSENT vers le bas tandis que les zones live
   *  apparaissent au-dessus (demande utilisateur). FLIP sur la section #sec-groups
   *  + entrée en fondu des sections révélées. Comme l'intro, cette transition fait
   *  partie du déroulé d'ouverture FORCÉ (cf. startReplay(true)) — on l'anime donc
   *  même sous « réduire les animations », pour rester cohérent avec l'intro. */
  private revealAfterIntro(): void {
    const before = this.els.groups.getBoundingClientRect().top;
    this.revealLiveSections();
    this.renderAll();
    // Les groupes étaient en haut ; ils sont maintenant poussés vers le bas par
    // le focus/Aujourd'hui. On les fait GLISSER depuis leur ancienne position
    // (offset borné pour rester élégant) jusqu'à leur place définitive.
    const raw = before - this.els.groups.getBoundingClientRect().top;
    const dy = Math.sign(raw) * Math.min(Math.abs(raw), 240);
    if (dy) {
      const g = this.els.groups;
      g.style.transition = "none";
      g.style.transform = `translateY(${dy}px)`;
      void g.offsetHeight; // reflow : COMMIT la position de départ — pas de rAF
      // (qui ne se déclenche pas dans un onglet masqué → glissement bloqué).
      g.style.transition = "transform .6s cubic-bezier(.22,1,.36,1)";
      g.style.transform = "";
      // Nettoyage gardé (transitionend BULLE depuis les enfants : on ne réagit
      // qu'à NOTRE transform sur la section) + filet temporisé pour ne JAMAIS
      // rester coincé en translateY si l'onglet est masqué (pas de transitionend).
      const clearSlide = (): void => {
        g.style.transition = "";
        g.style.transform = "";
        g.removeEventListener("transitionend", onSlideEnd);
      };
      const onSlideEnd = (e: TransitionEvent): void => {
        if (e.target === g && e.propertyName === "transform") clearSlide();
      };
      g.addEventListener("transitionend", onSlideEnd);
      setTimeout(clearSlide, 800);
    }
    for (const el of [this.els.focus, this.els.today]) {
      el.classList.add("reveal-in");
      // idem : animationend bulle (battement de la carte live, etc.) — on ne
      // retire la classe que pour NOTRE animation d'entrée, sur CET élément.
      const clearEnter = (): void => {
        el.classList.remove("reveal-in");
        el.removeEventListener("animationend", onEnter);
      };
      const onEnter = (e: AnimationEvent): void => {
        if (e.target === el && e.animationName === "revealIn") clearEnter();
      };
      el.addEventListener("animationend", onEnter);
      setTimeout(clearEnter, 700);
    }
  }

  private revealLiveSections(): void {
    this.els.today.classList.remove("hide");
    this.els.probs.classList.remove("hide");
    this.els.elo.classList.remove("hide");
    // live/thirds/bracket gèrent leur propre visibilité dans renderAll
  }

  private renderAll(): void {
    const nowIso = new Date().toISOString();
    this.els.today.classList.remove("hide"); // visible AVANT le rendu (mesure du débordement)
    renderTodayBand(this.els.today, this.official, nowIso, this.dayOffset);
    renderLiveFocus(this.els.focus, this.official, this.focus);
    // Phase à élimination directe atteinte → la grille KO REMPLACE les tableaux de
    // groupes + meilleurs 3es (désormais figés/historiques) ; avant ça, l'inverse
    // (le bracket n'aurait que des placeholders 1A/2B…).
    const ko = this.koReached();
    this.els.groups.classList.toggle("hide", ko);
    this.els.thirds.classList.toggle("hide", ko);
    this.els.bracket.classList.toggle("hide", !ko);
    if (ko) {
      this.els.groups.innerHTML = "";
      this.els.thirds.innerHTML = "";
      renderBracket(this.els.bracket, this.official, this.mcOutput);
      // Re-cale APRÈS layout : quand la section KO est dévoilée dans le même frame
      // (groupes masqués juste avant), la 1re mesure de hauteur dans renderBracket
      // peut tomber à 0 → bracket aplati. Un re-fit au frame suivant la corrige.
      requestAnimationFrame(() => {
        if (!this.els.bracket.classList.contains("hide")) fitBracket(this.els.bracket);
      });
    } else {
      this.els.bracket.innerHTML = "";
      renderGroupsGrid(this.els.groups, this.official);
      renderThirdsTable(this.els.thirds, this.official);
    }
    renderTeamEfficiency(this.els.efficiency, this.official);
    this.els.probs.classList.remove("hide");
    renderProbsPanel(this.els.probs, this.mcOutput, this.mcMeta);
    this.paintLeaders();
    this.els.elo.classList.remove("hide");
    renderEloRanking(this.els.elo, this.official);
    this.renderFooter();
  }

  /** Rendu « par minute » : seules les zones live changent (Aujourd'hui + focus
   *  + pied). Groupes/Elo/bracket restent en place tant qu'aucun match ne se
   *  termine — préserve le DOM (focus, scroll) et évite de recalculer 48 lignes
   *  Elo à chaque tick. */
  private renderLive(): void {
    const nowIso = new Date().toISOString();
    this.els.today.classList.remove("hide"); // visible AVANT le rendu (mesure du débordement)
    renderTodayBand(this.els.today, this.official, nowIso, this.dayOffset);
    renderLiveFocus(this.els.focus, this.official, this.focus);
    this.renderFooter();
  }

  private renderFooter(): void {
    const gen = this.official.live?.generatedAt;
    this.els.foot.innerHTML = `
      ${t("foot.source")} : <strong>${esc(this.liveSource)}</strong>${gen ? ` (${esc(t("foot.dataFrom", { date: fmtShortDateTime(gen) }))})` : ""}
      · ${t("foot.lastCalc")} : ${this.mcMeta ? esc(fmtShortDateTime(new Date(this.mcMeta.computedAt).toISOString())) : "—"}
      · ${t("foot.tz")}
      <br>${t("foot.disclaimer")}`;
  }

  private startRotation(): void {
    this.rotateTimer = setInterval(() => {
      if (document.hidden) return; // onglet masqué : pas de rotation inutile
      if (!this.inReplay && this.official.liveNow.length > 1 && this.focus.pinned === null) {
        this.focus.rotationIndex = (this.focus.rotationIndex + 1) % this.official.liveNow.length;
        renderLiveFocus(this.els.focus, this.official, this.focus);
      }
    }, ROTATE_MS);
  }

  /** Fait vivre le compte à rebours de la carte « prochain match » à la seconde,
   *  sans re-rendre toute la carte (on ne touche qu'au texte du chrono). */
  private startCountdown(): void {
    this.countdownTimer = setInterval(() => {
      if (document.hidden) return; // onglet masqué : on gèle le chrono
      const cd = this.els.focus.querySelector<HTMLElement>(".lf-cd[data-kick]");
      const iso = cd?.dataset.kick;
      if (!cd || !iso) return;
      cd.textContent = fmtCountdown(Date.parse(iso) - Date.now());
    }, 1000);
  }

  dispose(): void {
    if (this.rotateTimer) clearInterval(this.rotateTimer);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    if (this.leaderTimer) clearInterval(this.leaderTimer);
    if (this.probsFallbackTimer) {
      clearTimeout(this.probsFallbackTimer);
      this.probsFallbackTimer = null;
    }
    this.replay?.cancel();
  }

  /** Résultats réels figés (pour le mode Simulation « depuis le réel »). */
  snapshotForSimulation(): { fixed: Record<number, FixedResult>; finishedCount: number } {
    return { fixed: this.buildFixedResults(), finishedCount: this.official.finished.length };
  }

  /** Base pour le mode Projection : Elo courant + résultats réels figés + scores
   *  des matchs EN COURS (pour alimenter le tableau projeté en direct). */
  projectionBase(): {
    elos: OfficialState["currentElos"];
    fixed: Record<number, FixedResult>;
    live: Record<number, readonly [number, number]>;
    finishedCount: number;
  } {
    const live: Record<number, readonly [number, number]> = {};
    for (const v of this.official.matches) {
      const ft = v.live?.score?.ft;
      if ((v.status === "live" || v.status === "paused") && ft) live[v.match.id] = ft;
    }
    return {
      elos: this.official.currentElos,
      fixed: this.buildFixedResults(),
      live,
      finishedCount: this.official.finished.length,
    };
  }

  /** État officiel courant (pour le tooltip de parcours au survol des drapeaux). */
  getOfficial(): OfficialState {
    return this.official;
  }

  private buildFixedResults(): Record<number, FixedResult> {
    const fixed: Record<number, FixedResult> = {};
    for (const m of this.official.finished) {
      const view = this.official.byId.get(m.id);
      const pens = view?.penalties;
      fixed[m.id] = {
        score: m.score,
        ...(pens ? { penaltiesWinner: pens.winner } : {}),
      };
    }
    return fixed;
  }

  /** Filet local : Monte Carlo dans le Web Worker (hébergement statique, ou
   *  si le serveur ne pousse pas de probas à temps). */
  private async runWorkerSimulation(): Promise<void> {
    const nSims = defaultSimCount();
    const finishedCount = this.official.finished.length;
    this.mcMeta = {
      nSims,
      elapsedMs: this.mcMeta?.elapsedMs ?? 0,
      computedAt: this.mcMeta?.computedAt ?? Date.now(),
      running: true,
      server: false,
    };
    if (!this.inReplay) renderProbsPanel(this.els.probs, this.mcOutput, this.mcMeta);
    try {
      const { output, elapsedMs } = await this.mc.simulate({
        elos: this.official.currentElos,
        fixedResults: this.buildFixedResults(),
        nSims,
        seed: `wc26-live-${finishedCount}`,
        params: poissonParams,
        modelHomeAdvantage,
        shootoutModel,
        gkRatings,
        gkDivisor,
      });
      // Le serveur a pu pousser ses probas entre-temps : ne pas les écraser.
      if (this.probsFinished === finishedCount && this.mcMeta?.server) return;
      this.mcOutput = output;
      this.probsFinished = finishedCount;
      this.mcMeta = { nSims, elapsedMs, computedAt: Date.now(), running: false, server: false };
      this.snapshotProbs(output);
      if (!this.inReplay) {
        renderProbsPanel(this.els.probs, this.mcOutput, this.mcMeta);
        if (this.koReached()) renderBracket(this.els.bracket, this.official, this.mcOutput);
        this.renderFooter();
      }
    } catch {
      // résultat obsolète (nouvelle simulation déjà lancée) : ignorer
    }
  }

  /** Historique des probabilités (specs/03 §3) — snapshot par re-simulation. */
  private snapshotProbs(output: SimulationOutput): void {
    try {
      const raw = localStorage.getItem(PROBS_HISTORY_KEY);
      const history: unknown[] = raw ? (JSON.parse(raw) as unknown[]) : [];
      history.push({
        date: new Date().toISOString(),
        finished: this.official.finished.length,
        top: Object.fromEntries(Object.entries(output.titleProb).slice(0, 12)),
      });
      localStorage.setItem(PROBS_HISTORY_KEY, JSON.stringify(history.slice(-80)));
    } catch {
      /* best effort */
    }
  }
}
